import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import LoadingSpinner from './LoadingSpinner';
import { API } from '../lib/api';

function CDLAdmin({ onClose }) {
  const [weapons, setWeapons] = useState(null);
  const [rules, setRules] = useState(null);
  const [maps, setMaps] = useState([]);
  const [activeTab, setActiveTab] = useState('weapons');
  const [loading, setLoading] = useState(true);
  const [showAddWeapon, setShowAddWeapon] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingMaps, setEditingMaps] = useState(false);

  // Form states
  const [weaponForm, setWeaponForm] = useState({
    name: '',
    weapon_class: '',
    description: '',
    attachments: '',
    image_url: '',
    category: 'assault_rifles'
  });

  const [ruleForm, setRuleForm] = useState({
    mode_key: '',
    name: '',
    description: '',
    scoring: '',
    duration: '',
    overtime: '',
    rules: ''
  });

  useEffect(() => {
    loadCDLData();
  }, []);

  const loadCDLData = async () => {
    setLoading(true);
    try {
      const [weaponsRes, rulesRes, mapsRes] = await Promise.all([
        axios.get(`${API}/cdl/weapons`),
        axios.get(`${API}/cdl/rules`),
        axios.get(`${API}/maps`)
      ]);
      setWeapons(weaponsRes.data);
      setRules(rulesRes.data);
      setMaps(mapsRes.data.maps);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWeapon = async () => {
    if (!weaponForm.name || !weaponForm.weapon_class || !weaponForm.description) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      await axios.post(`${API}/cdl/weapons`, {
        ...weaponForm,
        attachments: weaponForm.attachments.split(',').map(a => a.trim()).filter(a => a),
        image_url: weaponForm.image_url.trim()
      });
      toast.success('Arme ajoutée avec succès');
      setShowAddWeapon(false);
      setWeaponForm({ name: '', weapon_class: '', description: '', attachments: '', image_url: '', category: 'assault_rifles' });
      loadCDLData();
    } catch (e) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteWeapon = async (category, weaponName) => {
    if (!window.confirm(`Supprimer ${weaponName} ?`)) return;
    
    try {
      await axios.delete(`${API}/cdl/weapons/${category}/${weaponName}`);
      toast.success('Arme supprimée');
      loadCDLData();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handleAddRule = async () => {
    if (!ruleForm.mode_key || !ruleForm.name || !ruleForm.description) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      await axios.post(`${API}/cdl/rules`, {
        ...ruleForm,
        rules: ruleForm.rules.split('\n').map(r => r.trim()).filter(r => r)
      });
      toast.success('Règle ajoutée avec succès');
      setShowAddRule(false);
      setRuleForm({ mode_key: '', name: '', description: '', scoring: '', duration: '', overtime: '', rules: '' });
      loadCDLData();
    } catch (e) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteRule = async (modeKey) => {
    if (!window.confirm(`Supprimer cette règle ?`)) return;
    
    try {
      await axios.delete(`${API}/cdl/rules/${modeKey}`);
      toast.success('Règle supprimée');
      loadCDLData();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handleUpdateMaps = async () => {
    try {
      const normalizedMaps = maps.map((map) => ({
        name: map.name?.trim() || '',
        image: map.image?.trim() || '',
        description: map.description?.trim() || '',
        modes: Array.isArray(map.modes) ? map.modes : []
      }));

      await axios.put(`${API}/cdl/maps`, normalizedMaps);
      toast.success('Cartes mises à jour avec succès');
      setEditingMaps(false);
      loadCDLData();
    } catch (e) {
      const errorDetail = e?.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Erreur mise à jour');
    }
  };

  const handleMapChange = (idx, field, value) => {
    const newMaps = [...maps];
    if (field === 'modes') {
      newMaps[idx][field] = value.split(',').map(m => m.trim()).filter(m => m);
    } else {
      newMaps[idx][field] = value;
    }
    setMaps(newMaps);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in">
        <div className="bg-[#0a0a0a] border border-[#262626] p-8 max-w-6xl w-full corner-decoration relative">
          <LoadingSpinner text="Chargement administration CDL..." />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in overflow-y-auto">
      <div className="bg-[#0a0a0a] border border-[#262626] p-8 max-w-6xl w-full corner-decoration relative slide-in my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-primary font-black text-2xl uppercase">ADMINISTRATION CDL</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-white font-secondary text-xs uppercase"
          >
            FERMER
          </button>
        </div>

        <div className="flex gap-4 mb-6 border-b border-white/10 pb-4">
          <button
            onClick={() => setActiveTab('weapons')}
            className={`font-secondary text-xs uppercase px-4 py-2 transition-colors ${
              activeTab === 'weapons' 
                ? 'text-[#ff4500] border-b-2 border-[#ff4500]' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            ARMES
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`font-secondary text-xs uppercase px-4 py-2 transition-colors ${
              activeTab === 'rules' 
                ? 'text-[#ff4500] border-b-2 border-[#ff4500]' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            RÈGLES
          </button>
          <button
            onClick={() => setActiveTab('maps')}
            className={`font-secondary text-xs uppercase px-4 py-2 transition-colors ${
              activeTab === 'maps' 
                ? 'text-[#ff4500] border-b-2 border-[#ff4500]' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            CARTES
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto pr-4">
          {activeTab === 'weapons' && (
            <div>
              <button
                onClick={() => setShowAddWeapon(!showAddWeapon)}
                className="mb-6 bg-[#00ff9d] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#00ff9d]/90 transition-colors font-secondary text-sm"
              >
                + AJOUTER UNE ARME
              </button>

              {showAddWeapon && (
                <div className="bg-[#1a1a1a] border border-[#262626] p-6 mb-6 space-y-4">
                  <select
                    value={weaponForm.category}
                    onChange={(e) => setWeaponForm({...weaponForm, category: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  >
                    <option value="assault_rifles">Fusil d'Assaut</option>
                    <option value="smgs">Mitraillette</option>
                    <option value="snipers">Fusil de Précision</option>
                  </select>
                  <input
                    placeholder="Nom de l'arme"
                    value={weaponForm.name}
                    onChange={(e) => setWeaponForm({...weaponForm, name: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <input
                    placeholder="Classe (ex: Fusil d'Assaut)"
                    value={weaponForm.weapon_class}
                    onChange={(e) => setWeaponForm({...weaponForm, weapon_class: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <textarea
                    placeholder="Description"
                    value={weaponForm.description}
                    onChange={(e) => setWeaponForm({...weaponForm, description: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm h-24"
                  />
                  <input
                    placeholder="Accessoires (séparés par des virgules)"
                    value={weaponForm.attachments}
                    onChange={(e) => setWeaponForm({...weaponForm, attachments: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <input
                    placeholder="URL image de l'arme (affichée dans le guide CDL)"
                    value={weaponForm.image_url}
                    onChange={(e) => setWeaponForm({...weaponForm, image_url: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <button
                    onClick={handleAddWeapon}
                    className="w-full bg-[#ff4500] text-black font-bold uppercase px-6 py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-sm"
                  >
                    AJOUTER
                  </button>
                </div>
              )}

              {weapons && Object.entries(weapons).map(([category, weaponList]) => (
                <div key={category} className="mb-6">
                  <h4 className="font-primary font-bold text-lg uppercase text-[#00ff9d] mb-4">
                    {category === 'assault_rifles' ? 'Fusils d\'Assaut' : category === 'smgs' ? 'Mitraillettes' : 'Fusils de Précision'}
                  </h4>
                  {weaponList.map((weapon, idx) => (
                    <div key={idx} className="bg-[#1a1a1a] border border-[#262626] p-4 mb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-primary font-bold uppercase">{weapon.name}</h5>
                          <p className="font-secondary text-xs text-muted-foreground">{weapon.description}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteWeapon(category, weapon.name)}
                          className="text-red-500 hover:text-red-400 font-secondary text-xs uppercase"
                        >
                          SUPPR
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'rules' && (
            <div>
              <button
                onClick={() => setShowAddRule(!showAddRule)}
                className="mb-6 bg-[#00ff9d] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#00ff9d]/90 transition-colors font-secondary text-sm"
              >
                + AJOUTER UNE RÈGLE
              </button>

              {showAddRule && (
                <div className="bg-[#1a1a1a] border border-[#262626] p-6 mb-6 space-y-4">
                  <input
                    placeholder="Clé du mode (ex: hardpoint)"
                    value={ruleForm.mode_key}
                    onChange={(e) => setRuleForm({...ruleForm, mode_key: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <input
                    placeholder="Nom (ex: Hardpoint)"
                    value={ruleForm.name}
                    onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <textarea
                    placeholder="Description"
                    value={ruleForm.description}
                    onChange={(e) => setRuleForm({...ruleForm, description: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm h-20"
                  />
                  <input
                    placeholder="Scoring (ex: 250 points)"
                    value={ruleForm.scoring}
                    onChange={(e) => setRuleForm({...ruleForm, scoring: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <input
                    placeholder="Durée (ex: 5 minutes)"
                    value={ruleForm.duration}
                    onChange={(e) => setRuleForm({...ruleForm, duration: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <input
                    placeholder="Overtime"
                    value={ruleForm.overtime}
                    onChange={(e) => setRuleForm({...ruleForm, overtime: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                  />
                  <textarea
                    placeholder="Règles (une par ligne)"
                    value={ruleForm.rules}
                    onChange={(e) => setRuleForm({...ruleForm, rules: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm h-32"
                  />
                  <button
                    onClick={handleAddRule}
                    className="w-full bg-[#ff4500] text-black font-bold uppercase px-6 py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-sm"
                  >
                    AJOUTER
                  </button>
                </div>
              )}

              {rules && Object.entries(rules).map(([key, rule]) => (
                <div key={key} className="bg-[#1a1a1a] border border-[#262626] p-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h5 className="font-primary font-bold uppercase">{rule.name}</h5>
                      <p className="font-secondary text-xs text-muted-foreground">{rule.description}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteRule(key)}
                      className="text-red-500 hover:text-red-400 font-secondary text-xs uppercase"
                    >
                      SUPPR
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'maps' && (
            <div>
              <button
                onClick={() => setEditingMaps(!editingMaps)}
                className="mb-6 bg-[#00ff9d] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#00ff9d]/90 transition-colors font-secondary text-sm"
              >
                {editingMaps ? 'ANNULER' : 'MODIFIER LES CARTES'}
              </button>

              {editingMaps && (
                <button
                  onClick={handleUpdateMaps}
                  className="mb-6 ml-4 bg-[#ff4500] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-sm"
                >
                  SAUVEGARDER
                </button>
              )}

              {maps.map((map, idx) => (
                <div key={idx} className="bg-[#1a1a1a] border border-[#262626] p-6 mb-4">
                  {editingMaps ? (
                    <div className="space-y-4">
                      <input
                        placeholder="Nom de la carte"
                        value={map.name}
                        onChange={(e) => handleMapChange(idx, 'name', e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                      />
                      <input
                        placeholder="URL de l'image"
                        value={map.image}
                        onChange={(e) => handleMapChange(idx, 'image', e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                      />
                      <textarea
                        placeholder="Description"
                        value={map.description}
                        onChange={(e) => handleMapChange(idx, 'description', e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm h-20"
                      />
                      <input
                        placeholder="Modes (séparés par virgules)"
                        value={map.modes.join(', ')}
                        onChange={(e) => handleMapChange(idx, 'modes', e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-2 font-secondary text-sm"
                      />
                    </div>
                  ) : (
                    <div>
                      <img 
  src={map.image} 
  alt={map.name} 
  className="w-full max-h-[400px] object-contain mb-4"
/>

                      <h5 className="font-primary font-bold uppercase">{map.name}</h5>
                      <p className="font-secondary text-xs text-muted-foreground">{map.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CDLAdmin;
